import { ID } from '../../_kernel/ID'
import { Name } from '../../_kernel/Name'
import { z } from 'zod'
import { Model } from '../../_kernel/architecture'
import { Email } from '../../_kernel/Email'
import { Token } from '../../_kernel/Token'

export const UserValidation = z.object({
  id: z.instanceof(ID, { message: 'id is required' }),
  name: z.instanceof(Name, { message: 'Name is required' }).optional(),
  email: z.instanceof(Email, { message: 'Email is required' }),
  googleAccessToken: z.instanceof(Token).optional(),
})

export class User extends Model {
  static create({ id, name, email, googleAccessToken }: z.infer<typeof UserValidation>) {
    UserValidation.parse({ id, name, email, googleAccessToken })

    return new User(id, email, name, googleAccessToken)
  }

  static fromJSON(json: ReturnType<User['toJSON']>) {
    return User.create({
      id: ID.create({ value: json.id }),
      email: Email.create({ value: json.email }),
      ...(json.name ? { name: Name.create({ value: json.name }) } : {}),
      ...(json.googleAccessToken
        ? { googleAccessToken: Token.create({ value: json.googleAccessToken }) }
        : {}),
    })
  }

  static fromJSONOrNull(json: ReturnType<User['toJSON']>) {
    try {
      return User.fromJSON(json)
    } catch (error) {
      return null
    }
  }

  constructor(
    private readonly _id: ID,
    private readonly _email: Email,
    private readonly _name?: Name,
    private readonly _googleAccessToken?: Token
  ) {
    super()
  }

  get id() { return this._id.value } // prettier-ignore

  get name() { return this._name?.value ?? null} // prettier-ignore

  get email() { return this._email.value } // prettier-ignore

  get googleAccessToken() { return this._googleAccessToken?.value } // prettier-ignore

  getInitials() {
    return this.name?.charAt(0).toUpperCase()
  }

  async getEmailHash() {
    const encoder = new TextEncoder()
    const dataBuffer = encoder.encode(this.email)
    const hashBuffer = (await crypto?.subtle?.digest('SHA-256', dataBuffer)) ?? ''
    const hashArray = Array.from(new Uint8Array(hashBuffer))

    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
  }

  hasName(): this is User & { name: Name } {
    return !!this.name
  }

  async avatar() {
    if (!this.googleAccessToken) {
      return null
    }

    const info = await fetch(
      `https://www.googleapis.com/oauth2/v1/userinfo?alt=json&access_token=${this.googleAccessToken}`,
      {
        method: 'GET',
      }
    ).then(response => response.json())

    return info.picture
  }

  toJSON() {
    return {
      id: this.id,
      name: this.name,
      email: this.email,
      googleAccessToken: this.googleAccessToken,
    }
  }
}
